// Upload model for the ingestion loop (ADR-0020 Phase 1). The form holds a list of files; on submit
// they are validated and read into base64 `UploadDocument`s for `POST /analyses/from-documents`.
//
// Caps mirror the api route's guards (≤10 docs, per-doc + total body limits) so the user gets a
// friendly message before the request rather than a 413. `validateUpload` is pure (takes file
// metadata) so it is unit-testable in node; the actual byte read uses the browser `FileReader`.
import type { UploadDocument } from './api.js';

export const MAX_DOCS = 10;
/** Per-file cap: 10 MB → ~13.6M base64 chars, under the api's ~14M-char per-doc limit. */
export const MAX_DOC_BYTES = 10 * 1024 * 1024;
/** Total cap: 14 MB raw → ~18.7 MB base64, under the api's 20 MB route body limit. */
export const MAX_TOTAL_BYTES = 14 * 1024 * 1024;

/** The subset of `File` metadata `validateUpload` needs (so tests pass plain objects, no DOM). */
export interface PendingFileMeta {
  name: string;
  size: number;
}

function mb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Validate a pending file selection. Returns a user-facing message, or null when it's good to send. */
export function validateUpload(files: PendingFileMeta[]): string | null {
  if (files.length === 0) return 'Add at least one document.';
  if (files.length > MAX_DOCS) return `Too many documents — ${MAX_DOCS} maximum.`;
  const empty = files.find((f) => f.size === 0);
  if (empty) return `"${empty.name}" is empty.`;
  const tooBig = files.find((f) => f.size > MAX_DOC_BYTES);
  if (tooBig) {
    return `"${tooBig.name}" is too large (${mb(tooBig.size)}). Max ${mb(MAX_DOC_BYTES)} per file.`;
  }
  const total = files.reduce((sum, f) => sum + f.size, 0);
  if (total > MAX_TOTAL_BYTES) {
    return `Total upload too large (${mb(total)}). Keep it under ${mb(MAX_TOTAL_BYTES)}.`;
  }
  return null;
}

/** Read one `File` into a base64 `UploadDocument` via `FileReader` (browser only). */
export function fileToUploadDocument(file: File): Promise<UploadDocument> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read "${file.name}".`));
    reader.onload = () => {
      // readAsDataURL → "data:<type>;base64,<data>"; keep only the base64 payload.
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64: comma >= 0 ? result.slice(comma + 1) : '',
      });
    };
    reader.readAsDataURL(file);
  });
}

/** Read all selected files into `UploadDocument`s, preserving order. */
export function filesToUploadDocuments(files: File[]): Promise<UploadDocument[]> {
  return Promise.all(files.map(fileToUploadDocument));
}
